// Sign-in screen. Google + Microsoft OAuth kicked off by hitting the
// existing API endpoints which set a CSRF cookie + 302 to the provider.
// On return the provider hits /google-callback or /callback which our
// AuthCallback component exchanges for an Agami access_token.

import { useEffect, useState } from "react";
import { authBase } from "./oauth-base";

const NEXT_URL_KEY = "agami_post_login_next";

interface Props {
  /** Where to land the user after successful sign-in. Defaults to /. */
  next?: string;
}

export function Login({ next = "/" }: Props) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Surface any one-shot error left behind by the callback page.
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err) setError(humanError(err));
  }, []);

  const launchProvider = async (provider: "google" | "microsoft") => {
    // Stash the destination so the callback knows where to land.
    sessionStorage.setItem(NEXT_URL_KEY, next);
    setError(null);
    try {
      // POST returns {auth_url} and sets a CSRF cookie via Set-Cookie
      // (credentials:include lets the callback page send it back).
      const resp = await fetch(`${authBase()}/auth/${provider}/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      const data = (await resp.json().catch(() => null)) as { auth_url?: string } | null;
      if (!resp.ok || !data?.auth_url) {
        setError(`Failed to start ${provider} sign-in.`);
        return;
      }
      window.location.assign(data.auth_url);
    } catch (e) {
      console.error("OAuth start failed:", e);
      const detail = e instanceof Error ? e.message : String(e);
      setError(`Couldn't reach the sign-in service (${detail}). Try again, or contact support if this keeps happening.`);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-10">
          <img
            src="/images/logo_h.svg"
            alt="Agami"
            className="h-10"
          />
        </div>

        <div className="space-y-3">
          <ProviderButton provider="google" onClick={() => void launchProvider("google")} />
          <ProviderButton provider="microsoft" onClick={() => void launchProvider("microsoft")} />
        </div>

        {error && (
          <div className="mt-5 rounded-[12px] border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="mt-8 text-center text-sm">
          <a
            href="/signup.html"
            className="text-brand hover:underline"
          >
            New here? Try the demo →
          </a>
        </div>
      </div>
    </div>
  );
}

function ProviderButton({ provider, onClick }: { provider: "google" | "microsoft"; onClick: () => void }) {
  const label = provider === "google" ? "Continue with Google" : "Continue with Microsoft";
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full h-12 rounded-full border border-line bg-white text-neutral-800 text-[15px] font-medium flex items-center justify-center gap-3 hover:bg-chip hover:border-neutral-300 transition-colors"
    >
      {provider === "google" ? <GoogleMark /> : <MicrosoftMark />}
      {label}
    </button>
  );
}

function humanError(kind: string): string {
  if (kind === "oauth_state") return "Your sign-in link expired. Please try again.";
  if (kind === "no_access") return "This account doesn't have access to Agami. Talk to an admin.";
  if (kind === "callback_failed") return "Sign-in failed. Please try again.";
  return "Sign-in failed. Please try again.";
}

function GoogleMark() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.1 4 9.3 8.6 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35.1 26.8 36 24 36c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.3 39.4 16 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C40.9 35 44 30 44 24c0-1.3-.1-2.6-.4-3.5z"/>
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <rect x="2" y="2" width="9" height="9" fill="#F25022" />
      <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
      <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
      <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export const POST_LOGIN_KEY = NEXT_URL_KEY;
